import * as yup from 'yup';
import { DocumentNode } from 'graphql';
import { FormManager, useForm } from 'react-form-state-manager';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import {
  GetRefundDetailsQuery as GetRefundDetails,
  RefundPurchaseInput,
} from '__generated__/graphql';

import { getServerErrors } from '../../../../common/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import GetOrderQuery from './GetOrderQuery';
import GetParticipantOrdersQuery from './GetParticipantOrdersQuery';
import GetRefundDetailsQuery from './GetRefundDetailsQuery';
import RefundMutation from './RefundMutation';
import UI from '../../../../common/UI';
import useCurrencyInput from '../../../../common/useCurrencyInput';
import useLocale from '../../../../common/useLocale';
import useMutation from '../../../../api/useMutation';
import usePaymentMethods from '../../../../common/usePaymentMethods';
import useQuery from '../../../../api/useQuery';
import useValidation from '../../../../common/useValidation';

   type Order = GetRefundDetails['order'];
   type RefundOption = GetRefundDetails['order']['refund_options'][0];
   type Purchase = RefundOption['items'][0]['purchase'];

interface RefundOrderFormData {
  payment_id: string | null;
  purchases: RefundPurchaseInput[];
  cancel: boolean;
  confirm: boolean;
}

const refundSchema = yup.object({
  confirm: yup.bool().required().oneOf([true]),
});

export interface RefundOrderFormProps {
  order: Order;
  purchaseId?: string;
  refetchQueries?: DocumentNode[];
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const RefundOrderForm = ({
  order, purchaseId, onSuccess, onCancel, refetchQueries = [GetParticipantOrdersQuery, GetOrderQuery],
}: RefundOrderFormProps) => {
  const notifier = useNotifier();
  const { t } = useTranslation();
  const { formatCurrency, formatDate, parseDate } = useLocale();
  const { getPaymentMethodImageUrl, getPaymentMethodTitle } = usePaymentMethods();

  const getRefundPurchaseInputs = (refundOption: RefundOption, purchaseId?: string, emptyAmount = false) => (
    refundOption.items.map((item) => ({
      purchase: { id: item.purchase.id },
      amount: item.purchase.id === purchaseId || !emptyAmount ? item.amount : null,
    }))
  );

  const getValues = (order: Order, purchaseId?: string) => {
    const refundOption = order.refund_options.filter((refundOption) => {
      if (purchaseId) {
        return refundOption.items.filter((item) => item.purchase.id === purchaseId).length > 0;
      }

      return true;
    })[0];

    return {
      payment_id: refundOption?.payment.id,
      purchases: refundOption ? getRefundPurchaseInputs(refundOption, purchaseId, true) : [],
      cancel: false,
      confirm: false,
    };
  };

  const form = useForm<RefundOrderFormData>({
    values: getValues(order, purchaseId),
  });

  const [refundFailed, setRefundFailed] = useState(false);

  const [refund, { error, loading }] = useMutation(
    RefundMutation,
    {
      variables: {
        input: {
          payment: {
            id: form.values.payment_id,
          },
          purchases: form.values.purchases.filter(({ amount }) => !!amount),
          cancel: form.values.cancel,
        },
      },
      refetchQueries,
      awaitRefetchQueries: true,
      onCompleted: ({ refund }) => {
        if (refund.status !== 'pending' && refund.status !== 'processing' && refund.status !== 'refunded') {
          setRefundFailed(true);
        } else {
          onSuccess?.();

          notifier.success(t('notifications.payment_refunded'));
        }
      },
    },
  );

  const [selectedRefundOption, setSelectedRefundOption] = useState(order.refund_options.filter(
    (option) => option.payment.id === form.values.payment_id,
  )[0]);

  const [formKey, setFormKey] = useState(0);

  const selectAll = () => {
    form.set((values) => ({
      ...values,
      purchases: selectedRefundOption ? getRefundPurchaseInputs(selectedRefundOption) : [],
    }));

    setFormKey((key) => key + 1);
  };

  const deselectAll = () => {
    form.set((values) => ({
      ...values,
      purchases: values.purchases.map((purchase) => ({ ...purchase, amount: null })),
    }));

    setFormKey((key) => key + 1);
  };

  const getRefundableAmount = (option: RefundOption) => (
    Math.min(
      option.payment.refundable_amount,
      option.items.reduce((sum, { amount }) => sum + amount, 0),
    )
  );

  const getRefundableAmountForPurchase = (purchase: Purchase, purchaseRefundableAmount: number) => {
    const paymentRefundableAmount = selectedRefundOption ? selectedRefundOption.payment.refundable_amount : 0;

    const otherRefunds = form.values.purchases
      .filter(({ purchase: { id } }) => purchase.id !== id)
      .reduce((sum, { amount }) => sum + (amount || 0), 0);

    const paymentRefundableAmountLeft = Math.max(paymentRefundableAmount - otherRefunds, 0);

    return Math.min(paymentRefundableAmountLeft, purchaseRefundableAmount);
  };

  const setRefundOption = (option: RefundOption) => {
    setSelectedRefundOption(option);

    form.set((values) => ({
      ...values,
      payment_id: option.payment.id,
      purchases: option.items.map(({ purchase }) => ({
        purchase: {
          id: purchase.id,
        },
        amount: null,
      })),
    }));
  };

  const { valid, errors } = useValidation({
    schema: refundSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const refundedPurchases: Purchase[] = selectedRefundOption
    ? selectedRefundOption.items.reduce((purchases, { purchase }, index) => ([
      ...purchases,
      ...form.values.purchases[index]?.amount ? [purchase] : [],
    ]), []) : [];

  const totalRefundAmount = form.values.purchases.reduce((sum, { amount }) => sum + (amount || 0), 0);

  return (
    <UI.Form onSubmit={() => refund()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4>
            {t('refund_invoice_id', { invoiceId: order.invoice_id })}
          </UI.H4>
        </UI.Div>

        {order.refund_options.length === 0 && (
          <UI.Warning>
            {t('refund_not_available')}
          </UI.Warning>
        )}

        {order.refund_options.length > 0 && (
          <>
            <UI.HR />

            <UI.ServerErrors error={error} />

            {refundFailed && (
              <UI.Error>
                <UI.Strong sc={{ block: true }}>
                  {t('refund_failed')}
                </UI.Strong>
                {t('refund_failed_description')}
              </UI.Error>
            )}

            <UI.InputGroup sc={{ valid: !errors.payment_id, touched: form.touched.payment_id }}>
              <UI.InputLabel>
                {t('payment')}
                {' '}
                <UI.RequiredMark />
              </UI.InputLabel>

              <UI.FormGrid sc={{ gutter: 0.25 }}>
                {order.refund_options.map((option) => (
                  <UI.Radio
                    onChange={() => setRefundOption(option)}
                    checked={form.values.payment_id === option.payment.id}
                    sc={{ box: true }}
                    key={option.payment.id}
                  >
                    <UI.FlexContainer sc={{ alignItems: 'center', justifyContent: 'space-between' }}>
                      <UI.Div>
                        <img
                          src={getPaymentMethodImageUrl(option.payment.payment_method)}
                          alt={getPaymentMethodTitle(option.payment.payment_method)}
                          style={{
                            width: 24, height: 16, display: 'inline-block', marginTop: 3, marginBottom: -3,
                          }}
                        />
                        {' '}
                        <UI.Strong>
                          {formatCurrency(getRefundableAmount(option))}
                        </UI.Strong>
                        {' ('}
                        {option.payment.consumer_account && option.payment.consumer_account}
                        {!option.payment.consumer_account && getPaymentMethodTitle(option.payment.payment_method)}
                        {') '}
                      </UI.Div>
                      <UI.Div sc={{ muted: true, small: true, noWrap: true }}>
                        {formatDate(
                          parseDate(option.payment.status_changed_at, { timezone: 'UTC' }),
                          { format: 'display_compact_date_time' },
                        )}
                      </UI.Div>
                    </UI.FlexContainer>
                  </UI.Radio>
                ))}
              </UI.FormGrid>
            </UI.InputGroup>

            {selectedRefundOption && (
              <>
                <UI.Div>
                  <UI.FlexContainer sc={{ justifyContent: 'space-between' }}>
                    <UI.InputGroup sc={{ valid: refundedPurchases.length > 0 }}>
                      <UI.InputLabel sc={{ mb: 0 }}>
                        {t('refund_purchases')}
                        {' '}
                        <UI.RequiredMark />
                      </UI.InputLabel>
                    </UI.InputGroup>
                    <UI.Div>
                      <UI.Delimit>
                        <UI.A
                          role="button"
                          onClick={() => selectAll()}
                          sc={{ disabled: refundedPurchases.length === selectedRefundOption.items.length }}
                        >
                          {t('select_all')}
                        </UI.A>
                        <UI.A
                          role="button"
                          onClick={() => deselectAll()}
                          sc={{ disabled: refundedPurchases.length === 0 }}
                        >
                          {t('deselect_all')}
                        </UI.A>
                      </UI.Delimit>
                    </UI.Div>
                  </UI.FlexContainer>

                  <UI.InputDescription sc={{ mb: 1 }}>
                    {t('edit_amount_for_partial_refund')}
                  </UI.InputDescription>

                  <UI.FormGrid sc={{ gutter: 0.25 }}>
                    {selectedRefundOption.items.map(({ purchase, amount }, index) => (
                      <RefundPurchase
                        form={form}
                        index={index}
                        purchase={purchase}
                        refundableAmount={getRefundableAmountForPurchase(purchase, amount)}
                        key={[selectedRefundOption.payment.id, index, formKey].join()}
                      />
                    ))}
                  </UI.FormGrid>
                </UI.Div>

                <UI.Info>
                  <UI.FlexContainer sc={{ justifyContent: 'space-between' }}>
                    <UI.Strong>
                      {t('total_refund')}
                    </UI.Strong>
                    <UI.Strong>
                      {formatCurrency(totalRefundAmount)}
                    </UI.Strong>
                  </UI.FlexContainer>
                </UI.Info>

                <UI.InputGroup sc={{ valid: !errors.cancel, touched: form.touched.cancel }}>
                  <UI.Checkbox {...form.checkbox('cancel')}>
                    {t('cancel_selected_purchases')}
                  </UI.Checkbox>
                </UI.InputGroup>

                <UI.InputGroup sc={{ valid: !errors.confirm, touched: form.touched.confirm }}>
                  <UI.InputLabel>
                    {t('confirmation')}
                    {' '}
                    <UI.RequiredMark />
                  </UI.InputLabel>
                  <UI.Checkbox {...form.checkbox('confirm')}>
                    {t('confirm_refund_label', { amount: formatCurrency(totalRefundAmount) })}
                  </UI.Checkbox>
                </UI.InputGroup>
              </>
            )}
          </>
        )}

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('confirm')}
          enabled={!refundFailed}
          onClose={onCancel}
          loading={loading}
          valid={valid && totalRefundAmount > 0}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

interface RefundPurchaseProps {
  form: FormManager<RefundOrderFormData>;
  index: number;
  purchase: Purchase;
  refundableAmount: number;
}

const RefundPurchase = ({ form, index, purchase, refundableAmount }: RefundPurchaseProps) => {
  const currencyInput = useCurrencyInput({ max: refundableAmount });

  const { amount } = form.values.purchases[index];

  const amountInputRef = useRef<HTMLInputElement>();

  const setAmount = useCallback((amount: number | null) => {
    form.set(`purchases.${index}.amount`, amount);

    amountInputRef.current.value = amount !== null ? currencyInput.value(amount) : null;
  }, [currencyInput, form, index]);

  const togglePurchase = () => {
    setAmount(!amount ? refundableAmount : null);
  };

  useEffect(() => {
    if (amount === 0) {
      // Unset amount when entering 0
      setAmount(null);
    }
  }, [amount, refundableAmount, setAmount]);

  return (
    <UI.FormGrid sc={{ gutter: 0.25, columns: '1fr 120px' }}>
      <UI.Checkbox
        onChange={togglePurchase}
        checked={!!amount}
        disabled={!amount && !refundableAmount}
        sc={{ box: true, checkboxPositionV: 'top' }}
      >
        <UI.GridContainer sc={{ columns: '14px 1fr', gutter: 0.25 }} style={{ minWidth: 0 }}>
          <UI.Div>
            {purchase.ticket && (
              <UI.Icon>
                <UI.Icons.Ticket />
              </UI.Icon>
            )}
          </UI.Div>
          <UI.FlexContainer sc={{ justifyContent: 'space-between' }} style={{ minWidth: 0 }}>
            <UI.Truncate sc={{ fontWeight: purchase.ticket ? 500 : undefined }}>
              {purchase.ticket?.title || purchase.product?.title}
              {purchase.product_variant && ` (${purchase.product_variant.title})`}
            </UI.Truncate>
            {purchase.ticket && (
              <UI.Span sc={{ muted: true, ml: 2 }}>
                #
                {purchase.registration.sequence}
              </UI.Span>
            )}
          </UI.FlexContainer>
        </UI.GridContainer>
      </UI.Checkbox>
      <UI.CurrencyInput
        {...form.text(`purchases.${index}.amount`, {
          inputHandler: {
            parse: currencyInput.parse,
            format: currencyInput.value,
          },
        })}
        placeholder={currencyInput.value(refundableAmount)}
        ref={amountInputRef}
      />
    </UI.FormGrid>
  );
};

interface RefundOrderModalProps {
  orderId: string;
  purchaseId?: string;
  refetchQueries: DocumentNode[];
  onSuccess?: () => void;
  onCancel?: () => void;
}

const RefundOrderModal = ({ orderId, purchaseId, onSuccess, onCancel, refetchQueries }: RefundOrderModalProps) => {
  const { data, loading } = useQuery(
    GetRefundDetailsQuery,
    {
      variables: {
        order_id: orderId,
      },
      fetchPolicy: 'network-only',
      // nextFetchPolicy is necessary to make sure data is also refetched when reopening the modal after doing a refund.
      nextFetchPolicy: 'network-only',
    },
  );

  const order = data?.order;

  return (
    <UI.Modal maxWidth={800} close={onCancel}>
      {(!order || loading) && (
        <UI.PageLoader />
      )}
      {(order && !loading) && (
        <RefundOrderForm
          order={order}
          purchaseId={purchaseId}
          onSuccess={onSuccess}
          onCancel={onCancel}
          refetchQueries={refetchQueries}
        />
      )}
    </UI.Modal>
  );
};
interface UseRefundOrderModalProps {
  orderId: string;
  purchaseId?: string;
  open?: boolean;
  refetchQueries?: DocumentNode[];
}

const useRefundOrderModal = ({
  orderId, purchaseId, open = false, refetchQueries,
}: UseRefundOrderModalProps) => {
  const [openModal, closeModal] = useModal(() => (
    <RefundOrderModal
      orderId={orderId}
      purchaseId={purchaseId}
      onSuccess={closeModal}
      onCancel={closeModal}
      refetchQueries={refetchQueries}
    />
  ), [orderId, refetchQueries]);

  useEffect(() => {
    if (open) {
      openModal();
    } else {
      closeModal();
    }
  }, [open, openModal, closeModal]);

  return [openModal, closeModal];
};

export default useRefundOrderModal;
